import * as React from 'react';

const FOCUSABLE_SELECTOR = [
	'a[href]',
	'button:not([disabled])',
	'textarea:not([disabled])',
	'input:not([disabled])',
	'select:not([disabled])',
	'[tabindex]:not([tabindex="-1"])',
].join(',');

const getFocusable = (node: HTMLElement): HTMLElement[] =>
	Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
		(element) => element.tabIndex !== -1 && !element.hasAttribute('aria-hidden')
	);

export const useFocusTrap = (
	isActive: boolean,
	containerRef: React.RefObject<HTMLElement | null>,
	onEscape?: () => void
): void => {
	const onEscapeRef = React.useRef(onEscape);
	onEscapeRef.current = onEscape;

	React.useEffect(() => {
		if (!isActive) return undefined;

		const node = containerRef.current;
		if (!node) return undefined;

		const previouslyFocused = document.activeElement as HTMLElement | null;
		const initial = getFocusable(node)[0] ?? node;
		initial.focus();

		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				event.stopPropagation();
				onEscapeRef.current?.();
				return;
			}

			if (event.key !== 'Tab') return;

			const items = getFocusable(node);
			if (items.length === 0) {
				event.preventDefault();
				node.focus();
				return;
			}

			const first = items[0];
			const last = items[items.length - 1];
			if (event.shiftKey && document.activeElement === first) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && document.activeElement === last) {
				event.preventDefault();
				first.focus();
			}
		};

		document.addEventListener('keydown', onKeyDown, true);
		return () => {
			document.removeEventListener('keydown', onKeyDown, true);
			previouslyFocused?.focus();
		};
	}, [isActive, containerRef]);
};
